/**
 * Vincent B1036 (Eric 2026-02-26 SPEC STRICK PROD) — Modal de révision
 * manuelle depuis une sélection texte dans la synthèse.
 *
 * Complémentaire au fact-check automatique (B943 / B1033). L'utilisateur
 * a sélectionné un passage dans le corps de la synthèse, un bouton
 * flottant lui a proposé « Ajouter une révision manuelle », il clique →
 * ce modal s'ouvre avec le passage pré-rempli. Il saisit le texte
 * corrigé + la source URL + une raison courte (optionnelle) → POST vers
 * `/videos/{id}/synthesis/manual-revision` → refresh de la synthèse.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;


export default function ManualRevisionModal({ open, onClose, videoId, originalText, onSaved }) {
  const [correction, setCorrection] = useState("");
  const [source, setSource] = useState("");
  const [issue, setIssue] = useState("");
  const [saving, setSaving] = useState(false);
  const correctionRef = useRef(null);

  // Reset des champs à chaque ouverture (pas de fuite d'état d'une révision
  // précédente si le modal a été fermé sans sauvegarder).
  useEffect(() => {
    if (open) {
      setCorrection("");
      setSource("");
      setIssue("");
      setTimeout(() => correctionRef.current?.focus(), 50);
    }
  }, [open]);

  // Fermeture Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  if (!open) return null;

  const trimmedOriginal = (originalText || "").trim();
  const trimmedCorrection = correction.trim();
  const trimmedSource = source.trim();
  const sourceLooksValid = /^https?:\/\/\S+$/i.test(trimmedSource);
  const canSave = trimmedOriginal.length >= 3 && trimmedCorrection && sourceLooksValid && !saving;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    try {
      const { data: res } = await axios.post(
        `${API}/api/videos/${videoId}/synthesis/manual-revision`,
        {
          original_text: trimmedOriginal,
          suggested_correction: trimmedCorrection,
          source: trimmedSource,
          issue: issue.trim(),
        },
        { withCredentials: true },
      );
      toast.success("Révision manuelle ajoutée.");
      onSaved?.(res?.claim);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      data-testid="manual-revision-modal"
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-lg bg-white dark:bg-zinc-900 rounded-xl shadow-2xl border border-zinc-200 dark:border-zinc-700"
      >
        <header className="flex items-center justify-between p-4 border-b border-zinc-200 dark:border-zinc-700">
          <h3 className="text-sm font-bold text-zinc-900 dark:text-zinc-100">
            Ajouter une révision manuelle
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100"
            aria-label="Fermer"
            data-testid="manual-revision-modal-close"
          >
            <X size={16} weight="bold" />
          </button>
        </header>

        <div className="p-4 space-y-3 text-sm">
          <div>
            <label className="block text-[11px] font-semibold text-zinc-700 dark:text-zinc-300 mb-1">
              Passage original (sélection)
            </label>
            <blockquote
              className="text-[12px] italic text-zinc-800 dark:text-zinc-200 bg-zinc-100 dark:bg-zinc-800 rounded-md p-2 border-l-4 border-zinc-400 max-h-24 overflow-auto"
              data-testid="manual-revision-modal-original"
            >
              « {trimmedOriginal || "(sélection vide — sélectionne un passage avant d'ouvrir ce dialogue)"} »
            </blockquote>
          </div>

          <div>
            <label htmlFor="mrev-correction" className="block text-[11px] font-semibold text-zinc-700 dark:text-zinc-300 mb-1">
              Texte corrigé <span className="text-red-600">*</span>
            </label>
            <textarea
              id="mrev-correction"
              ref={correctionRef}
              rows={3}
              value={correction}
              onChange={(e) => setCorrection(e.target.value)}
              placeholder="La formulation exacte, telle qu'elle devrait apparaître…"
              className="w-full rounded-md border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-950 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              data-testid="manual-revision-modal-correction"
              required
            />
          </div>

          <div>
            <label htmlFor="mrev-source" className="block text-[11px] font-semibold text-zinc-700 dark:text-zinc-300 mb-1">
              Source (URL) <span className="text-red-600">*</span>
            </label>
            <input
              id="mrev-source"
              type="url"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              placeholder="https://…"
              className="w-full rounded-md border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-950 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              data-testid="manual-revision-modal-source"
              required
            />
            {source && !sourceLooksValid && (
              <p className="text-[11px] text-red-600 mt-1">
                L'URL doit commencer par http:// ou https://
              </p>
            )}
          </div>

          <div>
            <label htmlFor="mrev-issue" className="block text-[11px] font-semibold text-zinc-700 dark:text-zinc-300 mb-1">
              Raison courte (optionnel)
            </label>
            <input
              id="mrev-issue"
              type="text"
              value={issue}
              onChange={(e) => setIssue(e.target.value)}
              placeholder="Ex. Chiffre obsolète, article de loi mis à jour…"
              className="w-full rounded-md border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-950 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              data-testid="manual-revision-modal-issue"
            />
          </div>
        </div>

        <footer className="flex items-center justify-end gap-2 p-4 border-t border-zinc-200 dark:border-zinc-700">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-3 py-1.5 text-xs font-semibold text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-md disabled:opacity-50"
            data-testid="manual-revision-modal-cancel"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={!canSave}
            className="px-3 py-1.5 text-xs font-semibold bg-amber-600 text-white hover:bg-amber-500 rounded-md disabled:opacity-50"
            data-testid="manual-revision-modal-submit"
          >
            {saving ? "Enregistrement…" : "Ajouter la révision"}
          </button>
        </footer>
      </form>
    </div>
  );
}
